import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/App';
import getUserFromStorage from '@/utils/userHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for logo
const getLogoUrl = (url) => {
  if (!url) return '';
  // Already a full URL (R2 or external)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Local API path - prepend backend URL
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Other local path - prepend backend URL with /api
  if (url.startsWith('/')) return `${BACKEND_URL}/api${url}`;
  // Relative path without leading slash
  return `${BACKEND_URL}/api/uploads/${url}`;
};

import { 
  Globe, 
  Settings, 
  Palette, 
  CreditCard, 
  Save, 
  Loader2,
  Image,
  Type,
  Link2,
  Search,
  ExternalLink,
  Mail,
  Server,
  Key,
  Eye,
  EyeOff,
  TestTube2,
  Building2,
  Hotel,
  FileText,
  ChevronDown,
  Upload,
  Trash2,
  X
} from 'lucide-react';

const PortalSettings = () => {
  const user = getUserFromStorage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingPayment, setTestingPayment] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showRazorpaySecret, setShowRazorpaySecret] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoInputRef = useRef(null);
  const [settings, setSettings] = useState({
    portal_enabled: true,
    portal_domain: '',
    portal_name: 'Book Your Stay',
    portal_logo: '',
    portal_tagline: 'Find and book the perfect accommodation',
    default_booking_mode: 'instant',
    default_payment_mode: 'online_advance',
    advance_percentage: 20,
    allow_hotel_subdomain: true,
    seo_title: '',
    seo_description: '',
    primary_color: '#f97316',
    secondary_color: '#1e293b',
    // Extended Appearance Settings
    accent_color: '#d4a373',
    // Header Colors
    header_bg_color: '#2d3748',
    header_text_color: '#ffffff',
    // Banner/Hero Colors
    banner_bg_color: '#2d3748',
    banner_text_color: '#ffffff',
    hero_gradient_end: '#1a202c',
    hero_gradient_enabled: true,
    hero_gradient_direction: '135deg',
    // Footer Colors
    footer_bg_color: '#2d3748',
    footer_text_color: '#ffffff',
    // Typography
    font_family: 'Inter',
    heading_font: 'Inter',
    button_style: 'rounded',
    hero_title: '',
    hero_subtitle: '',
    footer_text: '',
    show_stats: true,
    // Browser Tab Settings
    browser_title: 'BIZIONIX HMS - Hotel Management System',
    // Competitor Price Display
    show_competitor_prices: false,
    // SMTP Settings
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    // Razorpay Settings
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: ''
  });

  // Reset logo error when URL changes
  useEffect(() => {
    setLogoError(false);
    setLogoLoaded(false);
  }, [settings.portal_logo]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/portal/admin/settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch portal settings:', error);
      toast.error('Failed to load portal settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/portal/admin/settings', settings);
      toast.success('Portal settings saved successfully');
      // Refresh settings to ensure UI is in sync
      await fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-orange-500" />
            Public Booking Portal Settings
          </h1>
          <p className="text-gray-500 mt-1">Configure the public booking portal for guests</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-portal-settings">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="domain" className="flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Domain
          </TabsTrigger>
          <TabsTrigger value="booking" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Booking
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Appearance
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-orange-500" />
                General Settings
              </CardTitle>
              <CardDescription>Basic portal configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Public Portal</Label>
                  <p className="text-sm text-gray-500">Allow guests to view and book properties online</p>
                </div>
                <Switch
                  checked={settings.portal_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, portal_enabled: checked })}
                  data-testid="portal-enabled-switch"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="portal_name">Portal Name</Label>
                  <div className="relative mt-1">
                    <Type className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="portal_name"
                      value={settings.portal_name}
                      onChange={(e) => setSettings({ ...settings, portal_name: e.target.value })}
                      placeholder="e.g., Book Your Stay"
                      className="pl-10"
                      data-testid="portal-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="portal_tagline">Tagline</Label>
                  <Input
                    id="portal_tagline"
                    value={settings.portal_tagline}
                    onChange={(e) => setSettings({ ...settings, portal_tagline: e.target.value })}
                    placeholder="Find and book the perfect accommodation"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Portal Logo</Label>
                <div className="mt-2 space-y-3">
                  {/* Logo Preview */}
                  {settings.portal_logo ? (
                    <div className="relative inline-flex items-center justify-center p-4 bg-gray-100 rounded-lg border min-w-[200px] min-h-[80px]">
                      {logoError ? (
                        <div className="text-center">
                          <span className="text-red-500 text-sm block">Failed to load image</span>
                          <span className="text-gray-400 text-xs block mt-1">URL may be invalid or inaccessible</span>
                        </div>
                      ) : (
                        <img 
                          src={getLogoUrl(settings.portal_logo)} 
                          alt="Portal Logo" 
                          className="max-h-16 max-w-[180px] w-auto h-auto"
                          style={{ objectFit: 'contain', aspectRatio: 'auto', display: logoLoaded ? 'block' : 'none' }}
                          onLoad={() => setLogoLoaded(true)}
                          onError={() => setLogoError(true)}
                        />
                      )}
                      {!logoLoaded && !logoError && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Loading...</span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                        onClick={() => setSettings({ ...settings, portal_logo: '' })}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-48 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                      <Image className="h-6 w-6" />
                    </div>
                  )}
                  
                  {/* Live Preview in Header Style */}
                  {settings.portal_logo && !logoError && logoLoaded && (
                    <div className="bg-slate-800 rounded-lg p-3 inline-flex items-center gap-2">
                      <span className="text-xs text-slate-400 mr-2">Header Preview:</span>
                      <img 
                        src={getLogoUrl(settings.portal_logo)} 
                        alt="Header Preview" 
                        className="h-8 max-w-[120px] w-auto"
                        style={{ objectFit: 'contain' }}
                      />
                    </div>
                  )}
                  
                  {/* Upload Button & URL Input */}
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      ref={logoInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('Logo must be less than 2MB');
                          return;
                        }
                        
                        setUploadingLogo(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('entity_type', 'portal');
                          const response = await api.post('/upload/logo', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          if (response.data.logo_url || response.data.url) {
                            const logoUrl = response.data.logo_url || response.data.url;
                            setSettings({ ...settings, portal_logo: logoUrl });
                            setLogoError(false);
                            setLogoLoaded(false);
                            toast.success(`Logo uploaded successfully${response.data.storage === 'r2' ? ' (Cloud Storage)' : ' (Local Storage)'}`);
                          } else {
                            toast.error('Upload succeeded but no URL returned');
                          }
                        } catch (error) {
                          console.error('Upload error:', error);
                          const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload logo';
                          toast.error(errorMsg);
                        } finally {
                          setUploadingLogo(false);
                          if (logoInputRef.current) logoInputRef.current.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Upload Logo</>
                      )}
                    </Button>
                    <span className="text-gray-400">or</span>
                    <div className="flex-1">
                      <Input
                        value={settings.portal_logo || ''}
                        onChange={(e) => setSettings({ ...settings, portal_logo: e.target.value })}
                        placeholder="Enter logo URL"
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Recommended: PNG/SVG, max 200x50 pixels, under 2MB</p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  SEO Settings
                </h4>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="seo_title">SEO Title</Label>
                    <Input
                      id="seo_title"
                      value={settings.seo_title || ''}
                      onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
                      placeholder="Book Hotels Online | Best Rates Guaranteed"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seo_description">SEO Description</Label>
                    <Textarea
                      id="seo_description"
                      value={settings.seo_description || ''}
                      onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
                      placeholder="Find and book the best hotels with our easy-to-use booking platform..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Browser Tab Title */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  Browser Tab Settings
                </h4>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="browser_title">Browser Tab Title</Label>
                    <Input
                      id="browser_title"
                      value={settings.browser_title || 'BIZIONIX HMS - Hotel Management System'}
                      onChange={(e) => setSettings({ ...settings, browser_title: e.target.value })}
                      placeholder="BIZIONIX HMS - Hotel Management System"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This title appears in the browser tab for the admin panel
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Domain Settings */}
        <TabsContent value="domain">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-orange-500" />
                Domain & DNS Configuration
              </CardTitle>
              <CardDescription>Configure custom domains for your booking portal</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary Portal Domain - White Label Style */}
              <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="portal_domain" className="text-blue-800 font-medium">Primary Portal Domain (White Label)</Label>
                    <p className="text-xs text-blue-600 mb-2">Point your own domain to the main booking portal</p>
                    <Input
                      id="portal_domain"
                      value={(settings.portal_domain || '').replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}
                      onChange={(e) => setSettings({ ...settings, portal_domain: e.target.value.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '') })}
                      placeholder="bookings.yourdomain.com"
                      className="bg-white"
                      data-testid="portal-domain-input"
                    />
                    {settings.portal_domain && (
                      <div className="mt-3 p-3 bg-white rounded border text-sm">
                        <p className="font-medium text-gray-700 mb-2">DNS Configuration Required:</p>
                        <div className="space-y-2 text-gray-600">
                          <p>Add a <span className="font-mono bg-gray-100 px-1 text-blue-700">CNAME</span> record pointing:</p>
                          <div className="flex items-center gap-2 font-mono text-xs bg-gray-50 p-2 rounded">
                            <span className="text-gray-500">From:</span>
                            <span className="text-blue-700 font-medium">{(settings.portal_domain || '').replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-gray-500">To:</span>
                            <span className="text-green-700 font-medium">portal.emergentagent.com</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hotel Subdomains Toggle */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-gray-600 mt-0.5" />
                    <div>
                      <Label className="text-base font-medium">Allow Hotel Subdomains</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Each hotel can have its own subdomain (e.g., <span className="font-mono text-xs bg-gray-200 px-1 rounded">hotelname.{(settings.portal_domain || 'yourdomain.com').replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}</span>)
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.allow_hotel_subdomain}
                    onCheckedChange={(checked) => setSettings({ ...settings, allow_hotel_subdomain: checked })}
                  />
                </div>
              </div>

              {/* Per-Hotel Custom Domain Info */}
              <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                <div className="flex items-start gap-3">
                  <Hotel className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-orange-800 font-medium">Per-Hotel White Label Domains</Label>
                    <p className="text-sm text-orange-700 mt-1">
                      Each hotel can also have its own dedicated custom domain (e.g., <span className="font-mono text-xs bg-orange-100 px-1 rounded">hotelname.com</span>)
                    </p>
                    <p className="text-xs text-orange-600 mt-2">
                      Configure hotel-specific domains in <strong>Property Listing → Configure → Basic Info</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* DNS Instructions Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">DNS Configuration Guide</span>
                    </div>
                    <ChevronDown className="h-5 w-5 text-gray-500 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="p-4 border-t bg-white">
                    <div className="space-y-4 text-sm">
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">Step 1: Add DNS Record</h5>
                        <p className="text-gray-600">Go to your domain registrar's DNS settings and add the following record:</p>
                        <div className="mt-2 bg-gray-50 rounded p-3 font-mono text-xs">
                          <div className="grid grid-cols-3 gap-2 text-gray-500">
                            <span>Type</span><span>Name/Host</span><span>Value/Target</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-1 font-medium">
                            <span className="text-blue-600">CNAME</span>
                            <span className="text-gray-800">{settings.portal_domain?.split('.')[0] || 'bookings'}</span>
                            <span className="text-green-600">portal.emergentagent.com</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">Step 2: Wait for Propagation</h5>
                        <p className="text-gray-600">DNS changes can take up to 24-48 hours to propagate globally.</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">Step 3: SSL Certificate</h5>
                        <p className="text-gray-600">Once DNS is verified, SSL certificates will be automatically provisioned.</p>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-gray-500 text-xs">
                          Need help? Contact support at <a href="mailto:support@example.com" className="text-orange-600 hover:underline">support@example.com</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              {/* Preview Links */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-gray-500" />
                  Preview Links
                </h4>
                <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 w-32">Main Portal:</span>
                    <a 
                      href={`/portal`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:underline font-mono text-xs"
                      data-testid="portal-preview-link"
                    >
                      {(settings.portal_domain || window.location.host).replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}/portal
                    </a>
                  </div>
                  {settings.allow_hotel_subdomain && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 w-32">Hotel Subdomain:</span>
                      <span className="text-gray-600 font-mono text-xs">hotelname.{(settings.portal_domain || 'yourdomain.com').replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Booking & Payment Settings */}
        <TabsContent value="booking">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-orange-500" />
                Default Booking & Payment Settings
              </CardTitle>
              <CardDescription>Configure default settings for all properties (can be overridden per property)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="default_booking_mode">Default Booking Mode</Label>
                  <Select
                    value={settings.default_booking_mode}
                    onValueChange={(value) => setSettings({ ...settings, default_booking_mode: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="booking-mode-select">
                      <SelectValue placeholder="Select booking mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instant Confirmation</SelectItem>
                      <SelectItem value="request">Request (Hotel Approves)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Instant: Bookings confirmed immediately. Request: Hotel must approve.
                  </p>
                </div>

                <div>
                  <Label htmlFor="default_payment_mode">Default Payment Mode</Label>
                  <Select
                    value={settings.default_payment_mode}
                    onValueChange={(value) => setSettings({ ...settings, default_payment_mode: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="payment-mode-select">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online_full">Full Payment Online</SelectItem>
                      <SelectItem value="online_advance">Advance Payment Online</SelectItem>
                      <SelectItem value="pay_at_hotel">Pay at Hotel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {settings.default_payment_mode === 'online_advance' && (
                <div>
                  <Label htmlFor="advance_percentage">Default Advance Percentage</Label>
                  <div className="flex items-center gap-4 mt-1">
                    <Input
                      id="advance_percentage"
                      type="number"
                      min="1"
                      max="100"
                      value={settings.advance_percentage}
                      onChange={(e) => setSettings({ ...settings, advance_percentage: parseInt(e.target.value) || 20 })}
                      className="w-24"
                      data-testid="advance-percentage-input"
                    />
                    <span className="text-gray-500">% of total booking amount</span>
                  </div>
                </div>
              )}

              {/* Competitor Price Display */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Price Comparison Settings
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div>
                      <Label className="text-base font-medium text-blue-800">Show Competitor Prices</Label>
                      <p className="text-sm text-blue-600 mt-1">
                        Display competitor prices (MakeMyTrip, Booking.com, etc.) on search results to show price advantage
                      </p>
                    </div>
                    <Switch
                      checked={settings.show_competitor_prices || false}
                      onCheckedChange={(checked) => setSettings({ ...settings, show_competitor_prices: checked })}
                      data-testid="show-competitor-prices-switch"
                    />
                  </div>
                  
                  {settings.show_competitor_prices && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        When enabled, guests will see competitor platform prices next to each room type, 
                        showing them the savings when booking directly with you.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Note</h4>
                <p className="text-sm text-yellow-700">
                  These are default settings. Each hotel can override these in their property listing settings.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email/SMTP Settings */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-orange-500" />
                Email Configuration
              </CardTitle>
              <CardDescription>Configure SMTP settings for booking confirmation emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Email Notifications</Label>
                  <p className="text-sm text-gray-500">Send booking confirmation emails to guests</p>
                </div>
                <Switch
                  checked={settings.smtp_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, smtp_enabled: checked })}
                  data-testid="smtp-enabled-switch"
                />
              </div>

              {settings.smtp_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="smtp_host">SMTP Host</Label>
                      <div className="relative mt-1">
                        <Server className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="smtp_host"
                          value={settings.smtp_host || ''}
                          onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="smtp_port">SMTP Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        value={settings.smtp_port || 587}
                        onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                        placeholder="587"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="smtp_username">SMTP Username</Label>
                      <Input
                        id="smtp_username"
                        value={settings.smtp_username || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                        placeholder="your-email@gmail.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_password">SMTP Password</Label>
                      <div className="relative mt-1">
                        <Input
                          id="smtp_password"
                          type={showSmtpPassword ? 'text' : 'password'}
                          value={settings.smtp_password || ''}
                          onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                          placeholder="••••••••"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="smtp_from_email">From Email</Label>
                      <Input
                        id="smtp_from_email"
                        type="email"
                        value={settings.smtp_from_email || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                        placeholder="bookings@yourdomain.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_from_name">From Name</Label>
                      <Input
                        id="smtp_from_name"
                        value={settings.smtp_from_name || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                        placeholder="Book Your Stay"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Use TLS</Label>
                      <p className="text-sm text-gray-500">Enable TLS encryption (recommended)</p>
                    </div>
                    <Switch
                      checked={settings.smtp_use_tls !== false}
                      onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked })}
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Gmail Setup Instructions</h4>
                    <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Enable 2-Step Verification in your Google Account</li>
                      <li>Go to Google Account → Security → App passwords</li>
                      <li>Generate an app password for &quot;Mail&quot;</li>
                      <li>Use that app password here (not your regular Gmail password)</li>
                    </ol>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment/Razorpay Settings */}
        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-orange-500" />
                Payment Gateway Configuration
              </CardTitle>
              <CardDescription>Configure Razorpay for online payments during booking</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Razorpay Payments</Label>
                  <p className="text-sm text-gray-500">Accept online payments for portal bookings</p>
                </div>
                <Switch
                  checked={settings.razorpay_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, razorpay_enabled: checked })}
                  data-testid="razorpay-enabled-switch"
                />
              </div>

              {settings.razorpay_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="razorpay_key_id">Razorpay Key ID</Label>
                      <div className="relative mt-1">
                        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="razorpay_key_id"
                          value={settings.razorpay_key_id || ''}
                          onChange={(e) => setSettings({ ...settings, razorpay_key_id: e.target.value })}
                          placeholder="rzp_live_xxxxxxxxxx"
                          className="pl-10 font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="razorpay_key_secret">Razorpay Key Secret</Label>
                      <div className="relative mt-1">
                        <Input
                          id="razorpay_key_secret"
                          type={showRazorpaySecret ? 'text' : 'password'}
                          value={settings.razorpay_key_secret || ''}
                          onChange={(e) => setSettings({ ...settings, razorpay_key_secret: e.target.value })}
                          placeholder="••••••••"
                          className="pr-10 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRazorpaySecret(!showRazorpaySecret)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showRazorpaySecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-800 mb-2">Important</h4>
                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                      <li><strong>Test Mode:</strong> Use keys starting with <code className="bg-yellow-100 px-1 rounded">rzp_test_</code> for testing</li>
                      <li><strong>Live Mode:</strong> Switch to <code className="bg-yellow-100 px-1 rounded">rzp_live_</code> keys for production</li>
                      <li>Get your keys from the <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="underline">Razorpay Dashboard</a></li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">Test Cards</h4>
                    <p className="text-sm text-green-700 mb-2">Use these cards in test mode:</p>
                    <ul className="text-sm text-green-700 space-y-1 font-mono">
                      <li>Success: 4111 1111 1111 1111</li>
                      <li>Failure: 4000 0000 0000 0002</li>
                    </ul>
                  </div>
                </>
              )}

              {!settings.razorpay_enabled && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <CreditCard className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-600">Enable Razorpay to accept online payments</p>
                  <p className="text-sm text-gray-500 mt-1">Guests will only see &quot;Pay at Hotel&quot; option</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Settings */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-orange-500" />
                Portal Appearance
              </CardTitle>
              <CardDescription>Customize the look and feel of your booking portal</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Primary Colors Section */}
              <div>
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  Primary Colors
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="primary_color">Primary Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="primary_color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        placeholder="#f97316"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Buttons, links, accents</p>
                  </div>

                  <div>
                    <Label htmlFor="secondary_color">Secondary Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="secondary_color"
                        value={settings.secondary_color}
                        onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.secondary_color}
                        onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                        placeholder="#1e293b"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Default dark background</p>
                  </div>

                  <div>
                    <Label htmlFor="accent_color">Accent Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="accent_color"
                        value={settings.accent_color || '#d4a373'}
                        onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.accent_color || '#d4a373'}
                        onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                        placeholder="#d4a373"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Highlights, badges</p>
                  </div>
                </div>
              </div>

              {/* Header Colors Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  Header Colors
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="header_bg_color">Header Background</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="header_bg_color"
                        value={settings.header_bg_color || settings.secondary_color || '#2d3748'}
                        onChange={(e) => setSettings({ ...settings, header_bg_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.header_bg_color || ''}
                        onChange={(e) => setSettings({ ...settings, header_bg_color: e.target.value })}
                        placeholder="Uses secondary color if empty"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Navigation bar background</p>
                  </div>

                  <div>
                    <Label htmlFor="header_text_color">Header Text Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="header_text_color"
                        value={settings.header_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, header_text_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.header_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, header_text_color: e.target.value })}
                        placeholder="#ffffff"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Logo text, nav links</p>
                  </div>
                </div>
              </div>

              {/* Banner/Hero Colors Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  Banner / Hero Section
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="banner_bg_color">Banner Background</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="banner_bg_color"
                        value={settings.banner_bg_color || settings.secondary_color || '#2d3748'}
                        onChange={(e) => setSettings({ ...settings, banner_bg_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.banner_bg_color || ''}
                        onChange={(e) => setSettings({ ...settings, banner_bg_color: e.target.value })}
                        placeholder="Uses secondary color if empty"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Hero section start color</p>
                  </div>

                  <div>
                    <Label htmlFor="banner_text_color">Banner Text Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="banner_text_color"
                        value={settings.banner_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, banner_text_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.banner_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, banner_text_color: e.target.value })}
                        placeholder="#ffffff"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Hero title, subtitle</p>
                  </div>
                </div>

                {/* Gradient Options */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-base font-medium">Enable Gradient</Label>
                      <p className="text-xs text-gray-500 mt-1">Add a gradient effect to the banner</p>
                    </div>
                    <Switch
                      checked={settings.hero_gradient_enabled !== false}
                      onCheckedChange={(checked) => setSettings({ ...settings, hero_gradient_enabled: checked })}
                    />
                  </div>
                  
                  {settings.hero_gradient_enabled !== false && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                      <div>
                        <Label htmlFor="hero_gradient_end">Gradient End Color</Label>
                        <div className="flex items-center gap-3 mt-1">
                          <input
                            type="color"
                            id="hero_gradient_end"
                            value={settings.hero_gradient_end || '#1a202c'}
                            onChange={(e) => setSettings({ ...settings, hero_gradient_end: e.target.value })}
                            className="w-12 h-10 rounded cursor-pointer border"
                          />
                          <Input
                            value={settings.hero_gradient_end || '#1a202c'}
                            onChange={(e) => setSettings({ ...settings, hero_gradient_end: e.target.value })}
                            placeholder="#1a202c"
                            className="flex-1 font-mono text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="hero_gradient_direction">Gradient Direction</Label>
                        <Select 
                          value={settings.hero_gradient_direction || '135deg'} 
                          onValueChange={(value) => setSettings({ ...settings, hero_gradient_direction: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select direction" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="to bottom">Top to Bottom ↓</SelectItem>
                            <SelectItem value="to right">Left to Right →</SelectItem>
                            <SelectItem value="to bottom right">Diagonal ↘</SelectItem>
                            <SelectItem value="135deg">135° Angle</SelectItem>
                            <SelectItem value="180deg">180° (Vertical)</SelectItem>
                            <SelectItem value="45deg">45° Angle</SelectItem>
                            <SelectItem value="to top">Bottom to Top ↑</SelectItem>
                            <SelectItem value="radial">Radial (Center)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Colors Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                  Footer Colors
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="footer_bg_color">Footer Background</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="footer_bg_color"
                        value={settings.footer_bg_color || settings.secondary_color || '#2d3748'}
                        onChange={(e) => setSettings({ ...settings, footer_bg_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.footer_bg_color || ''}
                        onChange={(e) => setSettings({ ...settings, footer_bg_color: e.target.value })}
                        placeholder="Uses secondary color if empty"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Footer section background</p>
                  </div>

                  <div>
                    <Label htmlFor="footer_text_color">Footer Text Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        id="footer_text_color"
                        value={settings.footer_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, footer_text_color: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={settings.footer_text_color || '#ffffff'}
                        onChange={(e) => setSettings({ ...settings, footer_text_color: e.target.value })}
                        placeholder="#ffffff"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Footer text, links</p>
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="footer_text">Custom Footer Text</Label>
                  <Input
                    id="footer_text"
                    value={settings.footer_text || ''}
                    onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                    placeholder="© 2026 Your Company. All rights reserved."
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for default copyright text</p>
                </div>
              </div>

              {/* Typography Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  Typography
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="font_family">Primary Font</Label>
                    <Select 
                      value={settings.font_family || 'Inter'} 
                      onValueChange={(value) => setSettings({ ...settings, font_family: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select font" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter (Modern)</SelectItem>
                        <SelectItem value="Poppins">Poppins (Friendly)</SelectItem>
                        <SelectItem value="Roboto">Roboto (Clean)</SelectItem>
                        <SelectItem value="Open Sans">Open Sans (Classic)</SelectItem>
                        <SelectItem value="Playfair Display">Playfair Display (Elegant)</SelectItem>
                        <SelectItem value="Montserrat">Montserrat (Bold)</SelectItem>
                        <SelectItem value="Lato">Lato (Professional)</SelectItem>
                        <SelectItem value="Source Sans Pro">Source Sans Pro (Readable)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Body text and general content</p>
                  </div>

                  <div>
                    <Label htmlFor="heading_font">Heading Font</Label>
                    <Select 
                      value={settings.heading_font || 'Inter'} 
                      onValueChange={(value) => setSettings({ ...settings, heading_font: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select font" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                        <SelectItem value="Oswald">Oswald</SelectItem>
                        <SelectItem value="Raleway">Raleway</SelectItem>
                        <SelectItem value="Merriweather">Merriweather</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Titles and headings</p>
                  </div>
                </div>
              </div>

              {/* Button Style Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  Button Style
                </h4>
                <div className="flex gap-4">
                  {['rounded', 'square', 'pill'].map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() => setSettings({ ...settings, button_style: style })}
                      className={`px-6 py-2 font-medium transition-all ${
                        settings.button_style === style 
                          ? 'ring-2 ring-orange-500 ring-offset-2' 
                          : 'hover:bg-gray-100'
                      }`}
                      style={{ 
                        backgroundColor: settings.primary_color,
                        color: 'white',
                        borderRadius: style === 'rounded' ? '8px' : style === 'pill' ? '50px' : '0px'
                      }}
                    >
                      {style.charAt(0).toUpperCase() + style.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hero Content Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  Hero Section Content
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="hero_title">Custom Hero Title</Label>
                    <Input
                      id="hero_title"
                      value={settings.hero_title || ''}
                      onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                      placeholder="Find Your Perfect Stay"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for default title</p>
                  </div>
                  <div>
                    <Label htmlFor="hero_subtitle">Custom Hero Subtitle</Label>
                    <Input
                      id="hero_subtitle"
                      value={settings.hero_subtitle || ''}
                      onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                      placeholder="Discover amazing hotels and book your dream vacation"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for default subtitle</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <Switch
                    id="show_stats"
                    checked={settings.show_stats !== false}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_stats: checked })}
                  />
                  <Label htmlFor="show_stats">Show Statistics Section</Label>
                </div>
              </div>

              {/* Live Preview */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-lg mb-4">Live Preview</h4>
                <div 
                  className="rounded-xl overflow-hidden shadow-lg"
                  style={{ 
                    fontFamily: settings.font_family || 'Inter',
                  }}
                >
                  {/* Header Preview */}
                  <div 
                    className="px-6 py-4 flex items-center justify-between"
                    style={{ backgroundColor: settings.header_bg_color || settings.secondary_color }}
                  >
                    <span 
                      className="font-bold" 
                      style={{ 
                        fontFamily: settings.heading_font || 'Inter',
                        color: settings.header_text_color || '#ffffff'
                      }}
                    >
                      {settings.portal_name || 'Book Your Stay'}
                    </span>
                    <div className="flex gap-2 items-center">
                      <span 
                        className="text-sm opacity-70"
                        style={{ color: settings.header_text_color || '#ffffff' }}
                      >
                        Sign in
                      </span>
                      <span 
                        className="px-3 py-1 text-sm text-white"
                        style={{ 
                          backgroundColor: settings.primary_color,
                          borderRadius: settings.button_style === 'rounded' ? '6px' : settings.button_style === 'pill' ? '50px' : '0px'
                        }}
                      >
                        Register
                      </span>
                    </div>
                  </div>
                  
                  {/* Hero/Banner Preview */}
                  <div 
                    className="px-6 py-12 text-center"
                    style={{ 
                      background: settings.hero_gradient_enabled !== false 
                        ? (settings.hero_gradient_direction === 'radial' 
                          ? `radial-gradient(circle, ${settings.banner_bg_color || settings.secondary_color} 0%, ${settings.hero_gradient_end || '#1a202c'} 100%)`
                          : `linear-gradient(${settings.hero_gradient_direction || '135deg'}, ${settings.banner_bg_color || settings.secondary_color} 0%, ${settings.hero_gradient_end || '#1a202c'} 100%)`)
                        : (settings.banner_bg_color || settings.secondary_color)
                    }}
                  >
                    <h2 
                      className="text-2xl font-bold mb-2"
                      style={{ 
                        fontFamily: settings.heading_font || 'Inter',
                        color: settings.banner_text_color || '#ffffff'
                      }}
                    >
                      {settings.hero_title || 'Find Your Perfect Stay'}
                    </h2>
                    <p 
                      className="mb-6 text-sm opacity-80"
                      style={{ color: settings.banner_text_color || '#ffffff' }}
                    >
                      {settings.hero_subtitle || settings.portal_tagline || 'Discover amazing hotels'}
                    </p>
                    <button
                      className="px-6 py-2 font-medium text-white"
                      style={{ 
                        backgroundColor: settings.primary_color,
                        borderRadius: settings.button_style === 'rounded' ? '8px' : settings.button_style === 'pill' ? '50px' : '0px'
                      }}
                    >
                      Search Properties
                    </button>
                  </div>

                  {/* Footer Preview */}
                  <div 
                    className="px-6 py-4 text-center text-sm"
                    style={{ 
                      backgroundColor: settings.footer_bg_color || settings.secondary_color,
                      color: settings.footer_text_color || '#ffffff'
                    }}
                  >
                    {settings.footer_text || `© ${new Date().getFullYear()} ${settings.portal_name || 'Book Your Stay'}. All rights reserved.`}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortalSettings;
